import type { ThematicMeeting } from '@/types/thematic-meetings';

import { useDownloadThematicMeetingCalendar } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { getDefaultInvitationStart } from '@/pages/ThematicMeetings/utils/defaultInvitationStart';
import { downloadFile } from '@/utils/fileDownload';
import { getFullName } from '@/utils/user';

// Characters forbidden in Windows/macOS file names: replaced so the downloaded file always saves
// cleanly regardless of what the employee's own name happens to contain.
const ILLEGAL_FILENAME_CHARACTERS = /[/\\:*?"<>|]/g;

const sanitizeFileNameSegment = (segment: string): string =>
    segment.replace(ILLEGAL_FILENAME_CHARACTERS, '-');

/**
 * Downloads a meeting's ICS file so it can be added to the user's own calendar app. Failures are
 * reported here and never rethrown: callers treat the download as a side effect that must not undo
 * or block what they were doing (the plan header's "Add to my calendar" menu).
 */
export const useMeetingCalendarDownload = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const { mutateAsync: fetchCalendar, isPending } = useDownloadThematicMeetingCalendar();

    const downloadCalendar = async (meeting: ThematicMeeting) => {
        try {
            // Always sent: a meeting without a date gets its invitation on this default slot, which
            // the manager moves in their calendar, and a date set on the server wins, even one this
            // page has not heard of yet.
            const blob = await fetchCalendar({
                id: meeting.id,
                startAt: getDefaultInvitationStart().toISOString(),
            });
            // The `.ics` extension lives in code, not the dictionary: it is a file-format detail,
            // not something that varies by language.
            const filenameBase = t('thematicMeetings.plan.calendarFileName', {
                employee: sanitizeFileNameSegment(getFullName(meeting.employee ?? undefined)),
            });
            downloadFile(blob, `${filenameBase}.ics`);
        } catch (error) {
            handleError({
                error,
                data: {
                    title: t('errors.genericTitle'),
                    message: t('thematicMeetings.plan.calendarError'),
                },
            });
        }
    };

    return { downloadCalendar, isDownloading: isPending };
};
